import React, { useMemo, useState } from "react";
import { FaCalendarAlt, FaFlask, FaTrophy, FaUserCog, FaUsers } from "react-icons/fa";
import { PILOT_RESOURCES } from "../resources";
import { buildOwnerDirectory, buildSalesTodos, pilotsForOwner } from "../salesSelectors";
import {
  formatNumber,
  formatPercent,
  PILOT_LENGTH_WEEKS,
  summarizePilots,
} from "../selectors";
import { Metric } from "./Metric";
import { PastPilotsTable } from "./PastPilotsTable";
import { PilotsTable } from "./PilotsTable";
import { ResourceLinks } from "./ResourceLinks";
import { TodoList } from "./TodoList";

function PilotMetrics({ summary }) {
  const busiestFde = summary.fdeLoad.find((entry) => entry.name !== "Unassigned");
  const fdeHint = [
    busiestFde ? `${busiestFde.name}: ${busiestFde.count}` : null,
    summary.unassignedCount ? `${summary.unassignedCount} without an FDE` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <section className="pl-metrics">
      <Metric
        icon={FaFlask}
        label="Running pilots"
        value={formatNumber(summary.runningCount)}
        hint={
          summary.overdueCount
            ? `${summary.overdueCount} past the ${PILOT_LENGTH_WEEKS}-week playbook`
            : `${PILOT_LENGTH_WEEKS}-week playbook`
        }
      />
      <Metric
        icon={FaUsers}
        label="Clients using Addy this week"
        value={`${formatNumber(summary.usingThisWeekCount)} of ${formatNumber(summary.linkedCount)}`}
        hint="Running pilots linked to an Addy account"
        tone="green"
      />
      <Metric
        icon={FaUserCog}
        label="FDEs on pilots"
        value={formatNumber(summary.fdeCount)}
        hint={fdeHint || "No FDEs assigned"}
        tone="violet"
      />
      <Metric
        icon={FaCalendarAlt}
        label="Average days in pilot"
        value={formatNumber(summary.avgDaysInPilot)}
        hint="Since the deal entered the Pilot stage"
        tone="amber"
      />
      <Metric
        icon={FaTrophy}
        label="Past pilot win rate"
        value={formatPercent(summary.winRate)}
        hint={`${summary.pastWon} won · ${summary.pastLost} lost`}
        tone="cyan"
      />
    </section>
  );
}

/**
 * The pilots tab, built for the deal owner: pick your name to see your pilots
 * and what to do next, then the whole pipeline of pilots below.
 */
export function SalesView({ active, past, loading, selectedOwnerId, onSelectOwner }) {
  const [expandedMine, setExpandedMine] = useState(null);
  const [expandedAll, setExpandedAll] = useState(null);
  const [showPast, setShowPast] = useState(false);
  const owners = useMemo(() => buildOwnerDirectory([...active, ...past]), [active, past]);
  const owner = owners.find((entry) => entry.id === selectedOwnerId) || null;
  const ownPilots = useMemo(() => pilotsForOwner(active, owner?.id), [active, owner]);
  const todos = useMemo(() => buildSalesTodos(ownPilots), [ownPilots]);
  const summary = useMemo(() => summarizePilots(active, past), [active, past]);
  const ownerLoad = useMemo(
    () =>
      owners.map((entry) => {
        const own = pilotsForOwner(active, entry.id);
        const ownTodos = buildSalesTodos(own);

        return {
          ...entry,
          pilotCount: own.length,
          todoCount: ownTodos.length,
          urgentCount: ownTodos.filter((todo) => todo.priority === "high").length,
        };
      }),
    [owners, active],
  );
  const toggleIn = (setExpanded) => (dealId) =>
    setExpanded((current) => (current === dealId ? null : dealId));
  const urgentCount = todos.filter((todo) => todo.priority === "high").length;

  return (
    <div className="pl-sales">
      <ResourceLinks title="Pilot files" links={PILOT_RESOURCES} />

      <section className="pl-card pl-card--pad pl-fde-picker">
        <div className="pl-field">
          <label className="pl-field-label" htmlFor="pl-owner-self">
            I am
          </label>
          <select
            id="pl-owner-self"
            className="pl-select"
            value={owner ? owner.id : ""}
            onChange={(event) => onSelectOwner(event.target.value || null)}
          >
            <option value="">Choose your name</option>
            {owners.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </select>
        </div>
        {owner ? (
          <div className="pl-chip-row">
            <span className="pl-chip">{formatNumber(ownPilots.length)} running pilots</span>
            <span className="pl-chip">
              {formatNumber(todos.length)} to-dos
              {urgentCount ? ` · ${urgentCount} urgent` : ""}
            </span>
          </div>
        ) : (
          <p className="pl-note">
            Deal owners: pick your name to see your pilots and what to do next.
            Your choice is remembered on this browser.
          </p>
        )}
      </section>

      {owner ? (
        <>
          <section className="pl-card">
            <header className="pl-card-head">
              <div>
                <h2>What needs doing</h2>
                <p>
                  From your pilots' HubSpot deals and how the client is using
                  Addy, most urgent first.
                </p>
              </div>
            </header>
            <TodoList todos={todos} />
          </section>

          <section className="pl-card">
            <header className="pl-card-head">
              <div>
                <h2>Your pilots</h2>
                <p>Open a pilot to see its HubSpot record and the client's usage.</p>
              </div>
            </header>
            <PilotsTable
              pilots={ownPilots}
              loading={false}
              expandedId={expandedMine}
              onToggle={toggleIn(setExpandedMine)}
              emptyMessage="You own no running pilots in HubSpot."
            />
          </section>
        </>
      ) : null}

      <PilotMetrics summary={summary} />

      <section className="pl-card">
        <header className="pl-card-head">
          <div>
            <h2>All running pilots</h2>
            <p>
              Open deals in the Pilot stage or on a pilot step of the enterprise
              playbook. Features are the ones mentioned in each deal's recent
              HubSpot notes.
            </p>
          </div>
        </header>
        <PilotsTable
          pilots={active}
          loading={loading}
          expandedId={expandedAll}
          onToggle={toggleIn(setExpandedAll)}
        />
      </section>

      <section className="pl-card">
        <header className="pl-card-head">
          <div>
            <h2>Deal owners</h2>
            <p>Running pilots and open to-dos per deal owner.</p>
          </div>
        </header>
        <div className="pl-table-wrap">
          <table className="pl-table">
            <thead>
              <tr>
                <th className="pl-left">Owner</th>
                <th>Running pilots</th>
                <th>To-dos</th>
                <th>Urgent</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {ownerLoad.map((row) => (
                <tr key={row.id} className={row.id === selectedOwnerId ? "is-open" : ""}>
                  <td className="pl-left pl-strong">{row.name}</td>
                  <td>{formatNumber(row.pilotCount)}</td>
                  <td>{formatNumber(row.todoCount)}</td>
                  <td>{row.urgentCount ? row.urgentCount : <span className="pl-muted">0</span>}</td>
                  <td>
                    <button
                      type="button"
                      className="pl-btn pl-btn--small"
                      onClick={() => onSelectOwner(row.id)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="pl-card">
        <header className="pl-card-head">
          <div>
            <h2>Past pilots</h2>
            <p>Closed deals that reached a pilot step: the win/loss record.</p>
          </div>
          <button
            type="button"
            className="pl-btn"
            aria-expanded={showPast}
            onClick={() => setShowPast((current) => !current)}
          >
            {showPast ? "Hide" : `Show ${formatNumber(past.length)}`}
          </button>
        </header>
        {showPast ? <PastPilotsTable pilots={past} /> : null}
      </section>
    </div>
  );
}
